import type {RefObject} from 'react';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';

/**
 * Shared two-pass, pre-paint measurement engine behind both ResponsiveRow modes.
 *
 * `value === null` is the "measuring" state: the consumer renders its FULL
 * content (the expanded region / every item) so real wrap positions exist in the
 * DOM, then the layout effect resolves `value` via `measure(row)` — all before
 * the browser paints, so the oversized pass never flashes (pair with
 * `overflow-clip` on the row). It re-measures on a ResizeObserver
 * (rAF-debounced) and whenever `remeasureKey` changes; nothing else.
 *
 * Generalized from the technique proven in TagCategoryFilterBar. `measure` MUST
 * be stable (wrap in `useCallback`) and MUST NOT return `null` — that value is
 * reserved for the measuring sentinel.
 */
export function useFitMeasurement<T>(
  measure: (row: HTMLDivElement | null) => T,
  remeasureKey?: string | number
): {rowRef: RefObject<HTMLDivElement | null>; value: T | null} {
  const rowRef = useRef<HTMLDivElement>(null);
  // null = "measuring": render the full content, resolve next layout pass.
  const [value, setValue] = useState<T | null>(null);
  const measuredKeyRef = useRef<string | number | undefined>(remeasureKey);
  const frameRef = useRef<number | null>(null);

  // Two pre-paint passes when content changes: reset to "measuring" (render the
  // full content so wrap positions exist), then resolve. Neither the measuring
  // pass nor an over-wide intermediate is ever painted.
  useLayoutEffect(() => {
    if (measuredKeyRef.current !== remeasureKey) {
      measuredKeyRef.current = remeasureKey;
      if (value !== null) {
        // oxlint-disable-next-line react/react-compiler -- two-pass measure/render state machine: null forces a measuring render before the fitted value is committed
        setValue(null);
        return;
      }
    }
    if (value === null) {
      setValue(measure(rowRef.current));
    }
  }, [remeasureKey, value, measure]);

  // A row resize is the only other thing that can change the fit. Flip back to
  // "measuring" (debounced to one measurement per frame); the layout effect
  // above re-resolves from the freshly rendered full content. Empty deps: the
  // refs are stable and the observer no-ops while rowRef.current is null on the
  // first render, so this sets up exactly once.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    // Only WIDTH changes the fit. Track it so content-driven HEIGHT changes —
    // a consumer expanding something in the collapsed slot, or the transient
    // render-all measuring pass — don't trigger a spurious re-measure (which
    // would remount that content and reset it).
    let lastWidth = -1;
    const observer = new ResizeObserver((entries) => {
      const width = entries?.[0]?.contentRect?.width ?? 0;
      if (width === lastWidth) {
        return;
      }
      lastWidth = width;
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => setValue(null));
    });
    observer.observe(row);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      observer.disconnect();
    };
  }, []);

  return {rowRef, value};
}
